import React, { useEffect, useState } from 'react';
import type { CmsPage } from '../types';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { RenderCmsBlockPreview, PageEditor } from '../components/Editor/PageEditor';
import { Edit3 } from 'lucide-react';
import { NotFoundPage } from './NotFoundPage';

interface CmsPageViewProps {
  slug: string;
  isAdmin: boolean;
  allPages: CmsPage[];
}

export const CmsPageView: React.FC<CmsPageViewProps> = ({ slug, isAdmin, allPages }) => {
  const [page, setPage] = useState<CmsPage | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setIsEditing(false);

    fetch(`/api/pages/${slug}`)
      .then((res) => {
        if (!res.ok) {
          if (res.status === 404) setNotFound(true);
          throw new Error('Page not found');
        }
        return res.json() as Promise<CmsPage>;
      })
      .then((data: CmsPage) => {
        setPage(data);
      })
      .catch((e) => {
        console.warn(`Error loading page /${slug}:`, e);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="container py-5 text-center text-secondary">
        <div className="spinner-border text-primary me-2" role="status"></div>
        Loading page content...
      </div>
    );
  }

  if (notFound || !page) {
    return <NotFoundPage requestedSlug={slug} isAdmin={isAdmin} allPages={allPages} />;
  }

  const breadcrumbs = [
    { label: 'Home', url: '/' },
    { label: page.pageName },
  ];

  return (
    <div className="cms-page-container">
      {/* Page Header */}
      <section className="page-hero border-bottom py-4">
        <div className="container">
          <div className="row align-items-center gy-3">
            <div className="col-lg-8">
              <Breadcrumbs items={breadcrumbs} />
              <h1 className="hero-title display-5 fw-bold mb-2">{page.pageName}</h1>
              {page.metaDescription && (
                <p className="lead text-secondary small mb-0">{page.metaDescription}</p>
              )}
            </div>
            <div className="col-lg-4 text-lg-end">
              {isAdmin && !isEditing && (
                <button
                  type="button"
                  className="btn btn-warning d-inline-flex align-items-center gap-2"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit3 size={16} /> Edit Page Content
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="container py-5">
        {isAdmin && isEditing && (
          <PageEditor
            initialPage={page}
            allPages={allPages}
            onSaveSuccess={(updated) => {
              setPage(updated);
              setIsEditing(false);
            }}
            onDeleteSuccess={() => {
              window.history.pushState(null, '', '/');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
            onExit={() => setIsEditing(false)}
          />
        )}

        {/* Regular Viewer */}
        {(!isAdmin || !isEditing) && (
          <div className="cms-page-canvas">
            {page.body && page.body.length > 0 ? (
              page.body.map((block, idx) => (
                <RenderCmsBlockPreview key={block.id || idx} block={block} />
              ))
            ) : (
              <div className="p-5 text-center border border-dashed rounded-4 my-4 text-secondary">
                <p className="fw-semibold mb-1">There is no content published on this page yet.</p>
                {isAdmin && (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm mt-3"
                    onClick={() => setIsEditing(true)}
                  >
                    Open Site Editor to Add Content
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
